import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/**
 * A reusable search bar component that provides debounced text input, emits search updates, and supports customizable placeholder, icon sizing, and styling.
 * @component
 * @selector app-searchbar
 * @example
 *   <app-searchbar
 *     [placeholder]="'Search...'"
 *     [searchTerm]="currentQuery"
 *     [iconSize]="'medium'"
 *     [customClass]="'my-search'"
 *     [debounceTime]="300"
 *     (searchChange)="onSearch($event)"
 *     (searchTermChange)="onSearchTermChange($event)">
 *   </app-searchbar>
 * @input string placeholder - Placeholder text shown in the input when empty (default: 'Search...').
 * @input string searchTerm - Two-way bindable current search term value (initially empty by default).
 * @input 'small'|'medium'|'large' iconSize - Size of the search icon; affects styling (default: 'medium').
 * @input string customClass - Optional custom CSS class applied to the component host for additional styling.
 * @input number debounceTime - Debounce interval in milliseconds used to delay emitting search changes (default: 300).
 * @output EventEmitter<string> searchChange - Emits the debounced search term whenever the user input stabilizes.
 * @output EventEmitter<string> searchTermChange - Emits the debounced search term for two-way binding compatibility.
 *
 * Key methods:
 * - onInputChange(value: string): Called on user input; pushes the raw value into an internal Subject for debouncing.
 * - onClearSearch(): Clears the current searchTerm, emits an empty string immediately (and via the subject), and resets the input.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Subscribes to an internal Subject and sets up a debounced pipeline (debounceTime + distinctUntilChanged) to update searchTerm and emit events.
 * - ngOnDestroy(): Unsubscribes from the search subscription and completes the Subject to avoid memory leaks.
 *
 * Implementation notes:
 * - Uses an internal RxJS Subject (searchSubject) and Subscription (searchSubscription) to implement debouncing.
 * - Emits both searchChange and searchTermChange to support components expecting either event name.
 */
@Component({
  selector: 'app-searchbar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './searchbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./searchbar.component.scss'],
})
export class SearchbarComponent implements OnInit, OnDestroy {
  @Input() placeholder = 'Search...';
  @Input() searchTerm = '';
  @Input() iconSize: 'small' | 'medium' | 'large' = 'medium';
  @Input() customClass?: string;
  @Input() debounceTime = 300; // Configurable debounce time
  /** `data-testid` stem: emits `<testId>-input` and `<testId>-clear-button`. */
  @Input() testId = 'search';

  @Output() searchChange = new EventEmitter<string>();
  @Output() searchTermChange = new EventEmitter<string>();

  // Search debouncing
  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  /**
   * Set up a debounced search subscription: listens to searchSubject, applies debounceTime and distinctUntilChanged, updates this.searchTerm, emits searchChange and searchTermChange events, and logs the debounced term.
   * @example
   * gOnInit()
   * void
   * @returns {void} Initializes the debounced search subscription and side effects; does not return a value.
   **/
  ngOnInit(): void {
    // Set up search debouncing
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(this.debounceTime), // Wait for specified time after user stops typing
        distinctUntilChanged() // Only emit if value has changed
      )
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.searchChange.emit(searchTerm);
        this.searchTermChange.emit(searchTerm);
        console.log('Debounced search term:', searchTerm);
      });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.searchSubject.complete();
  }

  onInputChange(value: string): void {
    // Use the search subject for debouncing
    this.searchSubject.next(value);
  }

  onClearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next(''); // Clear the debounced search
    this.searchChange.emit('');
    this.searchTermChange.emit('');
  }
}
